// src/pages/AdminPanel.js – Admin approves/rejects writer requests
// Updated: 2025-12-28
// FIX: Writer role is "writer" (legacy "power" displayed as "writer")

import React, { useEffect, useMemo, useState } from "react";
import "../App.css";
import { api } from "../api";
import { useUser } from "../UserContext";

function safeStr(v) {
  return typeof v === "string" ? v : "";
}

function roleLabel(role) {
  const r = String(role || "").toLowerCase().trim();
  if (r === "power") return "writer"; // legacy bridge
  if (r === "writer" || r === "admin" || r === "user") return r;
  return "user";
}

export default function AdminPanel({ onNavigate }) {
  const { user, refreshMe } = useUser();
  const role = String(user?.role || "").toLowerCase().trim();
  const isAdmin = role === "admin";

  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("pending"); // pending|approved|rejected|all
  const [requests, setRequests] = useState([]);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [busyEmail, setBusyEmail] = useState("");

  const listTitle = useMemo(() => {
    if (statusFilter === "all") return "All Writer Requests";
    return `${statusFilter.charAt(0).toUpperCase()}${statusFilter.slice(1)} Writer Requests`;
  }, [statusFilter]);

  async function load() {
    setErr("");
    setOk("");
    setLoading(true);
    try {
      const qs = statusFilter === "all" ? "" : `?status=${encodeURIComponent(statusFilter)}`;
      const data = await api(`/api/admin/writer-requests${qs}`, { method: "GET" });
      setRequests(Array.isArray(data?.requests) ? data.requests : []);
    } catch (ex) {
      setErr(ex?.message || "Failed to load requests.");
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!isAdmin) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin, statusFilter]);

  async function decide(email, decision) {
    const cleanEmail = safeStr(email).trim().toLowerCase();
    if (!cleanEmail) return;

    setErr("");
    setOk("");
    setBusyEmail(cleanEmail);

    const note =
      decision === "approve"
        ? "Approved as writer."
        : "Rejected. Please re-apply with clear justification.";

    try {
      await api("/api/admin/writer-requests/decide", {
        method: "PATCH",
        body: JSON.stringify({ email: cleanEmail, decision, note }),
      });

      setOk(`Decision saved: ${cleanEmail} -> ${decision === "approve" ? "APPROVED" : "REJECTED"}`);
      await load();
      await refreshMe();
    } catch (ex) {
      setErr(ex?.message || "Failed to apply decision.");
    } finally {
      setBusyEmail("");
    }
  }

  if (!isAdmin) {
    return (
      <div className="page">
        <div className="card" style={{ maxWidth: 780, margin: "0 auto" }}>
          <h2 style={{ marginTop: 0 }}>Admin Panel</h2>
          <p style={{ color: "#475569" }}>This page is restricted to Admin accounts.</p>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 10 }}>
            <button className="btn" type="button" onClick={() => onNavigate && onNavigate("admin")}>
              Back to Dashboard
            </button>
            <button className="btn" type="button" onClick={() => onNavigate && onNavigate("home")}>
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page">
      <div className="card" style={{ maxWidth: 1100, margin: "0 auto" }}>
        <h2 style={{ marginTop: 0 }}>Admin Panel</h2>
        <p style={{ marginTop: 6, color: "#475569" }}>
          Approve or reject Writer requests. Approving upgrades the user role to <b>writer</b>.
        </p>

        {err ? <div className="banner" style={{ marginTop: 12 }}>{err}</div> : null}
        {ok ? <div className="banner" style={{ marginTop: 12 }}>{ok}</div> : null}

        <div style={{ marginTop: 12, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontWeight: 800 }}>Filter:</span>

          <button className="btn" type="button" onClick={() => setStatusFilter("pending")} disabled={statusFilter === "pending"}>
            Pending
          </button>

          <button className="btn" type="button" onClick={() => setStatusFilter("approved")} disabled={statusFilter === "approved"}>
            Approved
          </button>

          <button className="btn" type="button" onClick={() => setStatusFilter("rejected")} disabled={statusFilter === "rejected"}>
            Rejected
          </button>

          <button className="btn" type="button" onClick={() => setStatusFilter("all")} disabled={statusFilter === "all"}>
            All
          </button>

          <button className="btn" type="button" onClick={load} style={{ marginLeft: 10 }}>
            Refresh
          </button>

          <button className="btn" type="button" onClick={() => onNavigate && onNavigate("admin")} style={{ marginLeft: 10 }}>
            Back to Dashboard
          </button>

          <button className="btn" type="button" onClick={() => onNavigate && onNavigate("home")}>
            Go Home
          </button>
        </div>

        <div style={{ marginTop: 14, fontWeight: 900 }}>{listTitle}</div>

        {loading ? <div className="banner" style={{ marginTop: 12 }}>Loading...</div> : null}

        {!loading && requests.length === 0 ? (
          <div style={{ marginTop: 12, color: "#475569" }}>
            No requests found for this filter.
          </div>
        ) : null}

        {!loading && requests.length > 0 ? (
          <div style={{ marginTop: 12, display: "grid", gap: 12 }}>
            {requests.map((r) => {
              const email = safeStr(r?.user?.email).toLowerCase();
              const status = safeStr(r?.status);
              const msg = safeStr(r?.message);

              return (
                <div key={r.id} className="card" style={{ border: "1px solid #e5e7eb" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                    <div>
                      <div style={{ fontWeight: 900 }}>
                        {safeStr(r?.user?.username) || "user"}{" "}
                        <span style={{ color: "#475569", fontWeight: 600 }}>
                          ({email || "no-email"})
                        </span>
                      </div>

                      <div style={{ marginTop: 6 }}>
                        Current role:{" "}
                        <span className="pill" style={{ marginLeft: 8 }}>
                          {roleLabel(r?.user?.role) || "user"}
                        </span>

                        <span style={{ marginLeft: 10 }}>
                          Request:
                          <span className="pill" style={{ marginLeft: 8 }}>
                            {status || "unknown"}
                          </span>
                        </span>
                      </div>

                      {msg ? (
                        <div style={{ marginTop: 10, color: "#475569" }}>
                          Message: <b>{msg}</b>
                        </div>
                      ) : null}

                      {status === "rejected" && safeStr(r?.decisionNote) ? (
                        <div style={{ marginTop: 10, color: "#b45309" }}>
                          Decision note: <b>{safeStr(r?.decisionNote)}</b>
                        </div>
                      ) : null}
                    </div>

                    <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                      <button
                        className="btn"
                        type="button"
                        disabled={busyEmail === email || status === "approved"}
                        onClick={() => decide(email, "approve")}
                      >
                        {busyEmail === email ? "Working..." : "Approve"}
                      </button>

                      <button
                        className="btn"
                        type="button"
                        disabled={busyEmail === email || status === "rejected"}
                        onClick={() => decide(email, "reject")}
                      >
                        {busyEmail === email ? "Working..." : "Reject"}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : null}
      </div>
    </div>
  );
}
